'use client';

import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import useAuthStore from '@/store/useAuthStore';
import STORAGE_KEYS from '@/constant/storage';
import report, { ReportEvent } from '@/lib/report';
import { useState } from 'react';

interface SwitchToParentAccountModalProps {
  visible: boolean;
  onClose: () => void;
}

export default function SwitchToParentAccountModal({ visible, onClose }: SwitchToParentAccountModalProps) {
  const { signOut } = useAuthStore();
  const [isSwitching, setIsSwitching] = useState(false);

  if (!visible || typeof window === 'undefined') {
    return null;
  }

  const handleSwitch = async () => {
    setIsSwitching(true);
    try {
      report(ReportEvent.USER_QUEST_AVATAR_SIGN_OUT_CLICK, {});
      await signOut();
      localStorage.removeItem(STORAGE_KEYS.ONBOARD_DATA);
      localStorage.removeItem(STORAGE_KEYS.USER);
      onClose();
      window.location.href = '/';
    } finally {
      setIsSwitching(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)', zIndex: 1200 }}
      onClick={() => { if (!isSwitching) onClose(); }}
    >
      <div
        className="w-full max-w-[560px] rounded-[28px] border border-[#E8DDCF] bg-[#F8F5EE] px-6 pb-6 pt-7 shadow-[0_26px_80px_rgba(15,23,42,0.24)] animate-fade-in sm:px-8 sm:pb-8 sm:pt-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative flex items-start justify-center">
          <h3 className="display-text-bold w-full px-10 text-center text-2xl leading-tight text-[#111111] sm:text-[1.85rem]">
            Switch to parent account?
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-0 top-0 flex h-10 w-10 items-center justify-center rounded-full text-[#4B5563] transition-colors hover:bg-[#EEE9DF] hover:text-[#111827]"
            aria-label="Close switch parent dialog"
            disabled={isSwitching}
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </div>

        <p className="display-text mt-4 text-base leading-8 text-[#1e1e20] sm:text-[1.05rem]">
          You will be signed out. Please sign in with your parent account to continue.
        </p>

        <div className="mt-8 flex flex-col gap-3">
          <button
            type="button"
            onClick={handleSwitch}
            disabled={isSwitching}
            className="display-text-semi w-full rounded-xl bg-[#42C62C] py-3 text-sm text-white transition-colors hover:bg-[#38A824] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSwitching ? 'Logging Out...' : 'Log Out'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSwitching}
            className="display-text-semi w-full rounded-xl bg-[#ECEEF2] py-3 text-sm text-[#2F3A4A] transition-colors hover:bg-[#E2E6EC] disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
